import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { faUserClock, faUsers } from '@fortawesome/free-solid-svg-icons';
import {
  PENDING_HOST_APPLICATIONS,
  REVIEW_HOST_APPLICATION,
} from '../../services/authService/queries';
import {
  BRAND_HOSTS,
  RESUME_HOST_LISTINGS,
  REVOKE_HOST_APPROVAL,
  SUSPEND_HOST_LISTINGS,
} from '../../services/brandService/queries';
import { IUser } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { HostTripsDialog } from './HostTripsDialog';
import { openTripsLabel, pauseHostToast } from '../../utils/pausedHostTrips';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardRow,
  EmptyState,
  LoadingBlock,
  PageContainer,
  PageHeader,
  Stack,
  StatCard,
  StatGrid,
} from '../../components/ui';

const Person = styled.div`
  ${tw`min-w-0 flex-1`}
`;

const PersonName = styled.div`
  ${tw`flex flex-wrap items-center gap-2 text-sm font-semibold`}
  color: var(--color-text-primary);
`;

const PersonEmail = styled.div`
  ${tw`text-sm mt-0.5 break-all`}
  color: var(--color-text-secondary);
`;

const PersonMeta = styled.div`
  ${tw`text-xs mt-1`}
  color: var(--color-text-tertiary);
`;

const Actions = styled.div`
  ${tw`flex items-center gap-2 shrink-0`}
`;

function fullName(user: Partial<IUser> | undefined | null, fallback = ''): string {
  if (!user) return fallback;
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  return user.displayName || user.email || fallback;
}

export function AdminHostApplicationsPage() {
  const { data, loading, error } = useQuery(PENDING_HOST_APPLICATIONS);
  const [reviewApplication] = useMutation(REVIEW_HOST_APPLICATION, {
    refetchQueries: [{ query: PENDING_HOST_APPLICATIONS }, { query: BRAND_HOSTS }],
  });
  const [reviewingUserId, setReviewingUserId] = useState<string | null>(null);

  // D35, D41: approved hosts on this brand, with pause / resume / remove.
  const { data: hostsData, loading: hostsLoading, error: hostsError } = useQuery(BRAND_HOSTS);
  const hostMutationOptions = { refetchQueries: [{ query: BRAND_HOSTS }] };
  const [suspendHost] = useMutation(SUSPEND_HOST_LISTINGS, hostMutationOptions);
  const [resumeHost] = useMutation(RESUME_HOST_LISTINGS, hostMutationOptions);
  const [revokeHost] = useMutation(REVOKE_HOST_APPROVAL, hostMutationOptions);
  const [busyHostId, setBusyHostId] = useState<string | null>(null);
  // D41: the host whose open trips are shown in the Trips dialog.
  const [tripsHostId, setTripsHostId] = useState<string | null>(null);

  const runHostAction = async (
    userId: string,
    action: 'pause' | 'resume' | 'remove',
    label: string,
    openTripCount = 0,
  ) => {
    if (
      action === 'remove' &&
      !window.confirm(
        `Remove ${label}'s host approval on this brand? Their cars and locations stop showing here. Rejected while they still have an open trip on this brand.`,
      )
    ) {
      return;
    }
    try {
      setBusyHostId(userId);
      if (action === 'pause') {
        await suspendHost({ variables: { userId } });
        toast.success(pauseHostToast(label, openTripCount));
      } else if (action === 'resume') {
        await resumeHost({ variables: { userId } });
        toast.success(`${label}'s listings are live again on this brand.`);
      } else {
        await revokeHost({ variables: { userId } });
        toast.success(`${label} is no longer a host on this brand.`);
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update host');
    } finally {
      setBusyHostId(null);
    }
  };

  const handleReview = async (userId: string, approved: boolean) => {
    try {
      setReviewingUserId(userId);
      await reviewApplication({ variables: { userId, approved } });
      toast.success(approved ? 'Host application approved!' : 'Host application rejected.');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to review application');
    } finally {
      setReviewingUserId(null);
    }
  };

  const applications: IUser[] = data?.pendingHostApplications || [];
  const hosts: any[] = hostsData?.brandHosts ?? [];
  const pausedCount = hosts.filter(h => h.listingsSuspended).length;
  const tripsHost = tripsHostId ? hosts.find(h => h.userId === tripsHostId) : null;

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Admin"
        title="Hosts"
        description="Review applications in the order they arrived, then manage the hosts approved on this brand."
      />

      <Stack>
        <StatGrid $cols={3}>
          <StatCard label="Pending applications" value={loading ? '…' : applications.length} />
          <StatCard label="Approved hosts" value={hostsLoading ? '…' : hosts.length} />
          <StatCard
            label="Paused on this brand"
            value={hostsLoading ? '…' : pausedCount}
            hint={pausedCount > 0 ? 'Hidden from guests here' : undefined}
          />
        </StatGrid>

        <Card>
          <CardHeader
            title="Pending applications"
            description="First come, first served. Approval writes this brand's membership only."
          />
          {loading && <LoadingBlock label="Loading applications" />}
          {error && (
            <CardBody>
              <Alert tone="error" title="Could not load applications">
                {error.message}
              </Alert>
            </CardBody>
          )}
          {!loading && !error && applications.length === 0 && (
            <EmptyState
              icon={faUserClock}
              title="No pending applications"
              description="New host applications on this brand will appear here."
            />
          )}
          {!loading &&
            applications.map(app => (
              <CardRow key={app.id}>
                <Person>
                  <PersonName>
                    {fullName(app)}
                    <Badge tone="warning" dot size="sm">
                      Pending
                    </Badge>
                  </PersonName>
                  <PersonEmail>{app.email}</PersonEmail>
                  <PersonMeta>
                    ID {app.shortId} · Applied {new Date(app.createdAt).toLocaleDateString()}
                  </PersonMeta>
                </Person>
                <Actions>
                  <Button
                    text="Reject"
                    theme="secondary"
                    size="sm"
                    type="button"
                    onClick={() => handleReview(app.id, false)}
                    isDisabled={reviewingUserId === app.id}
                  />
                  <Button
                    text="Accept"
                    size="sm"
                    type="button"
                    onClick={() => handleReview(app.id, true)}
                    isLoading={reviewingUserId === app.id}
                  />
                </Actions>
              </CardRow>
            ))}
        </Card>

        <Card>
          <CardHeader
            title="Approved hosts on this brand"
            description="Pause hides a host's cars and locations from guests on this brand only and blocks new bookings here; booked trips continue and the host is emailed. Remove needs every trip on this brand ended or cancelled first."
          />
          {hostsLoading && <LoadingBlock label="Loading hosts" />}
          {hostsError && (
            <CardBody>
              <Alert tone="error" title="Could not load hosts">
                {hostsError.message}
              </Alert>
            </CardBody>
          )}
          {!hostsLoading && !hostsError && hosts.length === 0 && (
            <EmptyState
              icon={faUsers}
              title="No approved hosts yet"
              description="Hosts you approve above will be listed here."
            />
          )}
          {!hostsLoading &&
            hosts.map((membership: any) => {
              const host = membership.user ?? {};
              const label = fullName(host, membership.userId);
              const busy = busyHostId === membership.userId;
              const openTripCount: number = membership.openTripCount ?? 0;
              const openTrips = openTripsLabel(openTripCount);
              return (
                <CardRow key={membership.id} data-testid="brand-host-card">
                  <Person>
                    <PersonName>
                      {label}
                      {membership.listingsSuspended ? (
                        <Badge tone="warning" dot size="sm">
                          Paused
                        </Badge>
                      ) : (
                        <Badge tone="success" dot size="sm">
                          Listed
                        </Badge>
                      )}
                      {membership.isAdmin && (
                        <Badge tone="brand" size="sm">
                          Brand admin
                        </Badge>
                      )}
                      {membership.listingsSuspended && openTrips && (
                        <Badge tone="warning" size="sm" data-testid="host-open-trips-hint">
                          {openTrips} go ahead
                        </Badge>
                      )}
                    </PersonName>
                    <PersonEmail>{host.email}</PersonEmail>
                    <PersonMeta>
                      ID {host.shortId ?? membership.userId}
                      {membership.approvedAt
                        ? ` · Approved ${new Date(membership.approvedAt).toLocaleDateString()}`
                        : ''}
                      {` · ${openTrips ?? 'No open trips'}`}
                    </PersonMeta>
                  </Person>
                  <Actions>
                    <Button
                      text="Trips"
                      theme="text"
                      size="sm"
                      type="button"
                      onClick={() => setTripsHostId(membership.userId)}
                      dataTestId={`host-trips-${membership.userId}`}
                    />
                    {membership.listingsSuspended ? (
                      <Button
                        text="Resume"
                        size="sm"
                        type="button"
                        isDisabled={busy}
                        onClick={() => runHostAction(membership.userId, 'resume', label)}
                      />
                    ) : (
                      <Button
                        text="Pause"
                        theme="secondary"
                        size="sm"
                        type="button"
                        isDisabled={busy}
                        onClick={() =>
                          runHostAction(membership.userId, 'pause', label, openTripCount)
                        }
                      />
                    )}
                    <Button
                      text="Remove"
                      theme="text"
                      size="sm"
                      type="button"
                      isDisabled={busy}
                      onClick={() => runHostAction(membership.userId, 'remove', label)}
                    />
                  </Actions>
                </CardRow>
              );
            })}
        </Card>
      </Stack>
      {tripsHost && (
        <HostTripsDialog
          userId={tripsHost.userId}
          hostLabel={fullName(tripsHost.user ?? {}, tripsHost.userId)}
          hostPaused={Boolean(tripsHost.listingsSuspended)}
          onClose={() => setTripsHostId(null)}
        />
      )}
    </PageContainer>
  );
}

export default AdminHostApplicationsPage;
